// Iter 62bh — PWA update banner. Detects a new deploy by polling
// `/api/version` every 60s and, if the server boot timestamp changed since
// initial page load, shows a bottom banner "Nouvelle version dispo".
// Tap → skip waiting SW + hard reload with cache clear.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import { RefreshCw, X } from './icons';

export default function UpdateBanner() {
  const [outdated, setOutdated] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [reloading, setReloading] = useState(false);
  const initialVersion = useRef(null);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const { data } = await api.get('/version');
        const v = data?.version;
        if (!v) return;
        if (initialVersion.current == null) {
          initialVersion.current = v;
          return;
        }
        if (alive && v !== initialVersion.current) {
          setOutdated(true);
        }
      } catch (_) { /* silent — offline etc. */ }
    };
    check();
    const iv = setInterval(check, 60_000);
    // Also re-check when tab regains focus (user came back to app)
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      alive = false;
      clearInterval(iv);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  const doReload = async () => {
    setReloading(true);
    try {
      // 1) Tell any waiting service worker to activate NOW
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg?.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        // Ask SW to check for update too
        try { await reg?.update?.(); } catch (_) { /* ignore */ }
      }
      // 2) Nuke Cache Storage so the next fetch hits the network
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (_) { /* ignore */ }
    // 3) Hard reload (bypass memory cache too)
    window.location.reload();
  };

  if (!outdated || dismissed) return null;

  const banner = (
    <div
      data-testid="asa-update-banner"
      style={{
        position: 'fixed',
        left: 12, right: 12,
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)', // above mobile bottom-nav
        zIndex: 9999,
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 14px',
        borderRadius: 14,
        background:
          'radial-gradient(120% 100% at 100% 0%, rgba(91,190,252,0.24) 0%, rgba(91,190,252,0) 55%),'
          + ' linear-gradient(160deg, rgba(30,40,84,0.98) 0%, rgba(14,20,50,0.99) 100%)',
        border: '1.5px solid rgba(91,190,252,0.55)',
        boxShadow: '0 22px 44px -18px rgba(91,190,252,0.65), 0 8px 22px -10px rgba(0,0,0,0.7)',
        maxWidth: 460, marginInline: 'auto',
        animation: 'asa-slide-up 0.35s cubic-bezier(0.22,1,0.36,1)',
      }}
    >
      <style>{`
        @keyframes asa-slide-up {
          from { transform: translateY(60px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
      <span aria-hidden style={{
        width: 34, height: 34, borderRadius: 10, flexShrink: 0,
        display: 'grid', placeItems: 'center',
        background: 'linear-gradient(135deg, rgba(91,190,252,0.35), rgba(91,190,252,0.10))',
        border: '1px solid rgba(91,190,252,0.55)',
        color: '#5BBEFC',
      }}>
        <RefreshCw size={16} strokeWidth={2.3} className={reloading ? 'spin' : undefined} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5,
          color: '#5BBEFC', letterSpacing: '0.14em', textTransform: 'uppercase',
        }}>Nouvelle version dispo</div>
        <div style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
          color: 'var(--t1)', marginTop: 2,
        }}>Touche pour recharger ASA</div>
      </div>
      <button
        data-testid="asa-update-reload"
        onClick={doReload}
        disabled={reloading}
        style={{
          padding: '8px 14px', borderRadius: 10,
          background: reloading ? 'rgba(91,190,252,0.25)' : 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
          border: 'none', color: '#0B1330',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
          cursor: reloading ? 'wait' : 'pointer',
          flexShrink: 0,
        }}
      >{reloading ? 'Recharge…' : 'Recharger'}</button>
      <button
        data-testid="asa-update-dismiss"
        onClick={() => setDismissed(true)}
        aria-label="Ignorer"
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--t3)', padding: 4, display: 'grid', placeItems: 'center',
        }}
      ><X size={14} /></button>
    </div>
  );
  if (typeof document === 'undefined') return banner;
  return createPortal(banner, document.body);
}
